import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCw,
  Unplug,
  CreditCard,
  Info
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import stripeLogo from "@/assets/stripe-logo.png";

interface StripeConnectionCardProps {
  ptaId: string | null;
  disabled?: boolean;
  disabledReason?: string;
  onConnectionChange?: () => void;
}

interface StripeAccount {
  id: string;
  pta_id: string;
  stripe_account_id: string;
  stripe_account_status: string;
  onboarding_completed: boolean;
  created_at: string;
}

const FAST_POLL_INTERVAL = 2000;
const FAST_POLL_MAX_DURATION = 30000;
const SLOW_POLL_INTERVAL = 15000;

export function StripeConnectionCard({ ptaId, disabled, disabledReason, onConnectionChange }: StripeConnectionCardProps) {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isSyncingStatus, setIsSyncingStatus] = useState(false);
  const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const fetchStripeAccount = useCallback(async (silent = false): Promise<StripeAccount | null> => {
    if (!ptaId) return null;
    if (!silent) setLoading(true);
    try {
      const { data, error } = await supabase
        .from('pta_stripe_accounts')
        .select('*')
        .eq('pta_id', ptaId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching Stripe account:', error);
      }
      if (isMountedRef.current) setStripeAccount(data || null);
      return data || null;
    } catch (err) {
      console.error('Error:', err);
      return null;
    } finally {
      if (!silent && isMountedRef.current) setLoading(false);
    }
  }, [ptaId]);

  const verifyAccount = useCallback(async (accountId: string): Promise<StripeAccount | null> => {
    if (!ptaId) return null;
    try {
      await supabase.functions.invoke('stripe-connect-onboard', {
        body: { ptaId, action: 'verify', accountId },
      });
    } catch (err) {
      console.error('Error verifying:', err);
    }
    return fetchStripeAccount(true);
  }, [ptaId, fetchStripeAccount]);

  // Polling: fast then slow until active
  const startPolling = useCallback((accountId: string) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (!isMountedRef.current) return;

    setIsSyncingStatus(true);
    const startedAt = Date.now();

    const poll = async () => {
      if (!isMountedRef.current) return;
      const account = await verifyAccount(accountId);

      if (!isMountedRef.current) return;

      if (account?.stripe_account_status === 'active') {
        setIsSyncingStatus(false);
        onConnectionChange?.();
        toast({ title: "Stripe Connected", description: "Your Stripe account is now active and ready to accept payments." });
        return;
      }

      const elapsed = Date.now() - startedAt;
      const interval = elapsed < FAST_POLL_MAX_DURATION ? FAST_POLL_INTERVAL : SLOW_POLL_INTERVAL;

      // After fast window ends, hide the big syncing spinner but keep background polling
      if (elapsed >= FAST_POLL_MAX_DURATION && isMountedRef.current) {
        setIsSyncingStatus(false);
      }

      pollTimerRef.current = setTimeout(poll, interval);
    };

    poll();
  }, [verifyAccount, onConnectionChange]);

  // Initial fetch
  useEffect(() => {
    if (ptaId) {
      fetchStripeAccount().then((account) => {
        // If already pending, start background polling
        if (account && account.stripe_account_status !== 'active' && account.stripe_account_id) {
          startPolling(account.stripe_account_id);
        }
      });
    } else {
      setLoading(false);
    }
  }, [ptaId]);

  // Detect stripe_onboard=complete URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_onboard') === 'complete' && ptaId) {
      const accountId = params.get('account_id');
      window.history.replaceState({}, '', window.location.pathname + '?tab=payments');
      if (accountId) {
        setConnecting(false);
        setIsSyncingStatus(true);
        startPolling(accountId);
      }
    }
  }, [ptaId, startPolling]);

  // Reset connecting state and start polling when user returns from new tab
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && connecting) {
        setTimeout(async () => {
          if (!isMountedRef.current) return;
          setConnecting(false);
          const account = await fetchStripeAccount(true);
          if (account?.stripe_account_id && ptaId) {
            startPolling(account.stripe_account_id);
          }
          onConnectionChange?.();
        }, 1500);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connecting, ptaId, fetchStripeAccount, startPolling, onConnectionChange]);

  const handleConnect = async () => {
    if (!ptaId) return;
    setConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-connect-onboard', {
        body: { ptaId },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err: any) {
      toast({
        title: "Connection Failed",
        description: err.message || "Failed to start Stripe connection",
        variant: "destructive",
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!stripeAccount) return;
    setDisconnecting(true);
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    try {
      const { error } = await supabase
        .from('pta_stripe_accounts')
        .delete()
        .eq('id', stripeAccount.id);

      if (error) throw error;
      setStripeAccount(null);
      setIsSyncingStatus(false);
      toast({ title: "Stripe Disconnected", description: "Your Stripe account has been disconnected" });
      onConnectionChange?.();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to disconnect Stripe", variant: "destructive" });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (!ptaId || !stripeAccount) return;
    setRefreshing(true);
    await verifyAccount(stripeAccount.stripe_account_id);
    setRefreshing(false);
    toast({ title: "Status Refreshed", description: "Stripe account status has been updated" });
  };

  const getStatusBadge = () => {
    if (!stripeAccount) return null;
    if (isSyncingStatus) {
      return (
        <Badge className="bg-muted text-muted-foreground border-border">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Syncing…
        </Badge>
      );
    }
    switch (stripeAccount.stripe_account_status) {
      case 'active':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" /> Active
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Pending
          </Badge>
        );
      case 'restricted':
        return (
          <Badge className="bg-orange-100 text-orange-800 border-orange-200">
            <AlertCircle className="h-3 w-3 mr-1" /> Restricted
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="pt-8">
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const isBusy = isSyncingStatus || connecting;

  return (
    <Card className={`shadow-card ${disabled ? 'opacity-60' : ''}`}>
      <CardContent className="pt-8 pb-8">
        {/* Logo centered at top */}
        <div className="flex justify-center mb-4">
          <img src={stripeLogo} alt="Stripe" className="h-32 w-auto object-contain -my-8" />
        </div>

        {/* Syncing overlay message */}
        {isSyncingStatus && stripeAccount && (
          <div className="flex flex-col items-center gap-2 mb-4 p-4 bg-muted/50 rounded-lg">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm font-medium text-muted-foreground">Finalising Stripe connection…</p>
            <p className="text-xs text-muted-foreground">This usually takes a few seconds</p>
          </div>
        )}

        {disabled && disabledReason ? (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{disabledReason}</AlertDescription>
          </Alert>
        ) : stripeAccount ? (
          <div className="space-y-4">
            <div className="flex justify-center mb-2">
              {getStatusBadge()}
            </div>

            <div className="grid gap-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Account ID</span>
                <span className="text-sm font-mono">
                  ****{stripeAccount.stripe_account_id.slice(-4)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-muted-foreground">Onboarding</span>
                <span className="text-sm">
                  {stripeAccount.onboarding_completed ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-muted-foreground">Linked on</span>
                <span className="text-sm">
                  {new Date(stripeAccount.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            {!stripeAccount.onboarding_completed && !isSyncingStatus && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Complete your Stripe onboarding to start accepting payments. Click "Refresh Status" after completing setup on Stripe.
                </AlertDescription>
              </Alert>
            )}

            {stripeAccount.stripe_account_status === 'active' && stripeAccount.onboarding_completed && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium mb-2">✓ Ready to accept payments</p>
                <p className="text-xs text-green-700">
                  Parents can now pay using credit cards, debit cards, Apple Pay, and Google Pay.
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={handleRefreshStatus} disabled={refreshing || isBusy}>
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh Status
              </Button>
              <Button variant="destructive" size="sm" onClick={handleDisconnect} disabled={disconnecting || isBusy}>
                <Unplug className="h-4 w-4 mr-2" />
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-2">Why connect Stripe?</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Accept credit cards, debit cards, Apple Pay, Google Pay</li>
                <li>• No setup fees — pay only per transaction</li>
                <li>• Funds paid directly to your PTA account</li>
                <li>• Trusted by millions of businesses worldwide</li>
              </ul>
            </div>

            <Button onClick={handleConnect} disabled={connecting} className="w-full">
              {connecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" /> Connect Stripe Account
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              You'll be redirected to Stripe to set up your account
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
